import React, { useState, useEffect } from "react";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import ProfileCard from "../common/profileCard";
import StudentSidebar from "../common/studentSidebar";
import { useTranslation } from "react-i18next";
import {
  Button,
  Radio,
  Progress,
  Card,
  Space,
  Typography,
  Spin,
  Modal,
} from "antd";
import { ClockCircleOutlined } from "@ant-design/icons";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { useHandleQuiz } from "../../../core/hooks/useHandleQuiz";
import {
  countQuizPages,
  extractQuestionAndAnswers,
} from "../../../core/common/ults/domParser";
import { Editor as TinyMCEEditor } from "tinymce";
import { all_routes } from "../../router/all_routes";
import { CustomEditor } from "../../../core/common/ults/editor";
import { MathText } from "../../../core/common/ults/MathText";
import { QuizQuestionContent } from "../../../core/common/ults/QuizQuestionContent";

const { Title } = Typography;

const StudentQuizQuestion = () => {
  const { t } = useTranslation();
  const id = Number(useParams().id);
  const location = useLocation();
  const { quizName, state, totalgrade } = location.state || {};
  const [page, setPage] = useState(0);
  const route = all_routes;
  const {
    quizAttemptDetails,
    isQuizAttemptDetailsLoading,
    submitAnswer,
    saveAnswer,
    finishQuiz,
  } = useHandleQuiz({
    attemptid: id,
    page: page,
  });

  const totalQuizQuestions = countQuizPages(
    quizAttemptDetails?.attempt?.layout
  );

  const quizQuestionAndAnswer = extractQuestionAndAnswers(
    quizAttemptDetails?.questions?.[0]?.type,
    quizAttemptDetails?.questions?.[0]?.html
  );

  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const hasTimeLimit = !!quizAttemptDetails?.attempt?.timecheckstate;
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [timeLeftInitialized, setTimeLeftInitialized] = useState(false);
  const [showFiveMinutesLeft, setShowFiveMinutesLeft] = useState(false);
  const [answers, setAnswers] = useState<number[]>([]);
  const [answeredQuestions, setAnsweredQuestions] = useState<Set<number>>(
    new Set()
  );
  const [isFinishModalVisible, setIsFinishModalVisible] = useState(false);
  const initDataToSubmit = {
    attemptId: id,
    name: "",
    value: undefined as string | undefined,
    sequencecheck: "",
    sequencecheckValue: "",
  };
  const [dataToSubmit, setDataToSubmit] = useState<typeof initDataToSubmit>(initDataToSubmit);

  const navigate = useNavigate();

  useEffect(() => {
    if (quizAttemptDetails?.attempt?.timecheckstate) {
      const currentTime = Math.floor(Date.now() / 1000);
      setTimeLeft(quizAttemptDetails.attempt.timecheckstate - currentTime);
      setTimeLeftInitialized(true);
    }
  }, [quizAttemptDetails?.attempt?.timecheckstate]);

  useEffect(() => {
    if (timeLeft !== null && timeLeft > 0) {
      const timerId = setInterval(() => {
        setTimeLeft((prev) => (prev !== null && prev > 0 ? prev - 1 : 0));
      }, 1000);
      return () => clearInterval(timerId);
    }
  }, [timeLeft]);

  useEffect(() => {
    if (timeLeft !== null && timeLeft <= 300 && timeLeft > 0) {
      setShowFiveMinutesLeft(true);
    } else {
      setShowFiveMinutesLeft(false);
    }
  }, [timeLeft]);

  useEffect(() => {
    if (
      timeLeftInitialized &&
      timeLeft === 0 &&
      !!quizAttemptDetails?.attempt?.timecheckstate &&
      quizAttemptDetails?.attempt?.state === "inprogress"
    ) {
      handleConfirmFinish();
    }
  }, [
    timeLeft,
    timeLeftInitialized,
    quizAttemptDetails,
    finishQuiz,
    dataToSubmit,
    navigate,
    route.studentQuizReview,
    totalgrade,
    quizName,
  ]);

  useEffect(() => {
    if (
      quizQuestionAndAnswer?.selectedAnswerIndex !== null &&
      quizQuestionAndAnswer?.selectedAnswerIndex !== undefined
    ) {
      setSelectedAnswer(quizQuestionAndAnswer.selectedAnswerIndex);
      const newAnswers = [...answers];
      newAnswers[currentQuestion] = quizQuestionAndAnswer.selectedAnswerIndex;
      setAnswers(newAnswers);
      setAnsweredQuestions(
        (prev) => new Set(Array.from(prev).concat(currentQuestion))
      );
    } else {
      setSelectedAnswer(answers[currentQuestion] || null);
    }
  }, [quizQuestionAndAnswer?.selectedAnswerIndex, currentQuestion]);

  useEffect(() => {
    if (selectedAnswer !== null && selectedAnswer !== undefined) {
      setAnsweredQuestions(
        (prev) => new Set(Array.from(prev).concat(currentQuestion))
      );
    }
  }, [selectedAnswer, currentQuestion]);

  const handleEditorChange = (content: string, editor: TinyMCEEditor) => {
    const name = editor.id;
    setDataToSubmit((prevState: any) => ({
      ...prevState,
      [name]: content,
      name: quizQuestionAndAnswer?.answers?.[0]?.name,
      sequencecheck: quizQuestionAndAnswer?.sequenceCheck?.name,
      sequencecheckValue: quizQuestionAndAnswer?.sequenceCheck?.value,
    }));
    // Chỉ cập nhật answeredQuestions nếu content (value) không rỗng
    if (
      quizAttemptDetails?.questions?.[0]?.type === "essay" &&
      content &&
      content.trim() !== ""
    ) {
      setAnsweredQuestions(
        (prev) => new Set(Array.from(prev).concat(currentQuestion))
      );
    }
  };

  const submitCurrentAnswer = async () => {
    if (dataToSubmit.name && dataToSubmit.value) {
      try {
        await submitAnswer(dataToSubmit);
        await saveAnswer(dataToSubmit);
        setDataToSubmit(initDataToSubmit);
      } catch (error) {
        console.error("Lỗi khi submit answer:", error);
      }
    }
  };

  const handleAnswerSelect = (value: number) => {
    setSelectedAnswer(value);
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = value;
    setAnswers(newAnswers);
    setAnsweredQuestions(
      (prev) => new Set(Array.from(prev).concat(currentQuestion))
    );

    const selectedOption = quizQuestionAndAnswer?.answers?.[value];
    if (selectedOption) {
      setDataToSubmit({
        ...dataToSubmit,
        attemptId: quizAttemptDetails?.attempt?.id,
        name: selectedOption.name || "",
        value: selectedOption.value || "",
        sequencecheck: quizQuestionAndAnswer?.sequenceCheck?.name || "",
        sequencecheckValue: quizQuestionAndAnswer?.sequenceCheck?.value || "",
      });
    }
  };

  const handleNext = async () => {
    if (currentQuestion < totalQuizQuestions - 1) {
      await submitCurrentAnswer();

      setCurrentQuestion(currentQuestion + 1);
      setPage(currentQuestion + 1);
      setSelectedAnswer(answers[currentQuestion + 1] || null);
    }
  };

  const handlePrevious = async () => {
    if (currentQuestion > 0) {
      await submitCurrentAnswer();

      setCurrentQuestion(currentQuestion - 1);
      setPage(currentQuestion - 1);
      setSelectedAnswer(answers[currentQuestion - 1] || null);
    }
  };

  const handleQuestionSelect = async (index: number) => {
    await submitCurrentAnswer();
    setCurrentQuestion(index);
    setPage(index);
    setSelectedAnswer(answers[index] || null);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  const getQuestionButtonType = (index: number) => {
    if (index === currentQuestion) return "primary";
    if (answers[index] !== undefined) return "default";
    return "dashed";
  };

  const getQuestionButtonStyle = (index: number) => {
    const baseStyle = { width: "40px", height: "40px" };

    if (index === currentQuestion) {
      return {
        ...baseStyle,
        backgroundColor: "#1890ff",
        borderColor: "#1890ff",
        color: "white",
      };
    }

    const hasAnswer = answeredQuestions.has(index);

    if (hasAnswer) {
      return {
        ...baseStyle,
        backgroundColor: "#52c41a",
        borderColor: "#52c41a",
        color: "white",
      };
    }

    return {
      ...baseStyle,
      backgroundColor: "#f5f5f5",
      borderColor: "#d9d9d9",
      color: "#666",
    };
  };

  const handleFinishQuiz = async () => {
    await submitCurrentAnswer();
    setIsFinishModalVisible(true);
  };

  const handleConfirmFinish = async () => {
    try {
      const response = await finishQuiz(dataToSubmit?.attemptId);
      if (response) {
        setIsFinishModalVisible(false);
        console.log("Đã nộp bài thành công");
        navigate(
          `${route.studentQuizReview}/${quizAttemptDetails?.attempt?.id}`,
          { state: { totalgrade: totalgrade, quizName: quizName } }
        );
      }
    } catch (error) {
      console.error("Lỗi khi nộp bài:", error);
    }
  };

  const handleCancelFinish = () => {
    setIsFinishModalVisible(false);
  };

  return (
    <>
      <div className="content">
        <div className="container">
          <ProfileCard />
          <div className="row">
            <StudentSidebar />
            <div className="col-lg-9">
              <Breadcrumb title={t("quiz.title")} />

              <Card className="quiz-container">
                <div className="header-wrapper d-flex mb-3 justify-content-between align-items-center">
                  <h5>{quizName}</h5>
                  {hasTimeLimit && showFiveMinutesLeft && (
                    <div
                      style={{
                        color: "red",
                        fontWeight: "bold",
                      }}
                    >
                      Bạn còn {formatTime(timeLeft || 0)} để hoàn thành bài tập!
                    </div>
                  )}
                  <div>
                    <Button
                      type="primary"
                      onClick={() => navigate(route.studentQuiz)}
                    >
                      Quay lại
                    </Button>
                  </div>
                </div>
                <div className="quiz-header">
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <Title level={4}>
                        Câu hỏi {currentQuestion + 1}/{totalQuizQuestions}
                      </Title>
                      {hasTimeLimit &&
                        (state === "inprogress" ||
                          quizAttemptDetails?.attempt?.state) && (
                          <div className="timer">
                            <ClockCircleOutlined /> {formatTime(timeLeft || 0)}
                          </div>
                        )}
                    </div>
                    <Progress
                      percent={
                        ((currentQuestion + 1) / totalQuizQuestions) * 100
                      }
                      showInfo={false}
                    />
                  </Space>
                </div>
                <Spin spinning={isQuizAttemptDetailsLoading}>
                  <div className="question-navigation mt-3 mb-4">
                    <Space wrap>
                      {Array.from(
                        { length: totalQuizQuestions },
                        (_, index) => (
                          <Button
                            key={index}
                            type={getQuestionButtonType(index)}
                            onClick={() => handleQuestionSelect(index)}
                            style={getQuestionButtonStyle(index)}
                          >
                            {index + 1}
                          </Button>
                        )
                      )}
                    </Space>
                  </div>

                  <div className="quiz-content mt-4">
                    <Title level={5}>
                      <QuizQuestionContent
                        html={quizQuestionAndAnswer?.question}
                        imageSrcs={quizQuestionAndAnswer?.questionImageSrcs}
                      />
                    </Title>
                    {quizAttemptDetails?.questions?.[0]?.type === "essay" ? (
                      <CustomEditor
                        value={
                          dataToSubmit?.value !== undefined && dataToSubmit?.value !== null
                            ? dataToSubmit.value
                            : quizQuestionAndAnswer?.answers?.[0]?.value || ""
                        }
                        onEditorChange={handleEditorChange}
                        id="value"
                      />
                    ) : (
                      <Radio.Group
                        onChange={(e) => handleAnswerSelect(e.target.value)}
                        value={selectedAnswer}
                      >
                        <Space direction="vertical">
                          {quizQuestionAndAnswer?.answers?.map(
                            (option, index) => (
                              <Radio key={index} value={index}>
                                <MathText text={option?.text} isHtml />
                              </Radio>
                            )
                          )}
                        </Space>
                      </Radio.Group>
                    )}
                  </div>

                  <div className="quiz-footer mt-4">
                    <Space>
                      <Button
                        onClick={handlePrevious}
                        disabled={currentQuestion === 0}
                      >
                        Câu trước
                      </Button>
                      <Button
                        type="primary"
                        onClick={handleNext}
                        disabled={currentQuestion === totalQuizQuestions}
                      >
                        Câu tiếp theo
                      </Button>
                      <Button type="primary" danger onClick={handleFinishQuiz}>
                        Nộp bài
                      </Button>
                    </Space>
                  </div>
                </Spin>
              </Card>
            </div>
          </div>
        </div>
      </div>
      <Modal
        title="Xác nhận nộp bài"
        visible={isFinishModalVisible}
        onOk={handleConfirmFinish}
        onCancel={handleCancelFinish}
        okText="Đồng ý nộp bài"
        cancelText="Tiếp tục làm bài"
        okButtonProps={{
          danger: true,
          disabled: answeredQuestions.size !== totalQuizQuestions,
        }}
        centered
      >
        <div style={{ textAlign: "center" }}>
          <p style={{ fontSize: "16px", marginBottom: "16px" }}>
            Bạn có chắc chắn muốn nộp bài <strong>{quizName}</strong> không?
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <span>Tổng số câu hỏi:</span>
              <strong>{totalQuizQuestions}</strong>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "8px",
              }}
            >
              <span>Đã trả lời:</span>
              <strong style={{ color: "#52c41a" }}>
                {answeredQuestions.size}
              </strong>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Chưa trả lời:</span>
              <strong style={{ color: "#ff4d4f" }}>
                {totalQuizQuestions - answeredQuestions.size}
              </strong>
            </div>
          </div>

          <p style={{ color: "#666", fontSize: "14px" }}>
            Sau khi nộp bài, bạn sẽ không thể thay đổi đáp án nữa.
          </p>
        </div>
      </Modal>
    </>
  );
};

export default StudentQuizQuestion;
